import React, { Fragment, PureComponent } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Avatar from '../Avatar';
import AddClientOptions from '../AddClientOptions';
import ModalClientDetails from '../ModalClientDetails';
import ModalPortal from '../ModalPortal';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import './ClientsIndexView.css';
import * as analytics from '../../services/analytics';

const propTypes = {
  clients: PropTypes.array,
};

const defaultProps = {
  clients: [],
};

class ClientsIndexView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      brokerIsMe: 'no',
    };
  }

  componentDidMount() {
    analytics.page('Broker Portal - Clients List');
    if (this.props.authToken) {
      this.props.clearClientsList();
      this.props.getClients(this.clientsFilter);
      this.props.getClientsStats(this.clientsFilter);
    }
  }

  componentWillUnmount() {
    window.$(this.addClientOptionsModal).modal('hide');
    this.addModal.hide();
  }

  getStatus(client) {
    if (isEmpty(client.policies_stats)) {
      return (
        <span className="badge badge-warning text-uppercase">Prospect</span>
      );
    } else if (client.policies_stats.inforce) {
      return (
        <span className="badge badge-success text-uppercase">
          {client.policies_stats.inforce} in force
        </span>
      );
    } else if (client.policies_stats.churned) {
      return (
        <span className="badge badge-default text-uppercase">Churned</span>
      );
    }
  }

  handleAddClient = (clientDetails) => {
    const {
      createClient,
      onNotifySuccess,
      onNotifyError,
      clearClientsList,
      getClients,
      getClientsStats,
    } = this.props;

    analytics.track('Broker Portal - Add Client Submitted');
    createClient(clientDetails)
      .then(() => {
        analytics.track('Broker Portal - Add Client Succeeded', {
          noninteraction: 1,
        });
        const clientFullName = `${clientDetails.person.first_name} ${clientDetails.person.last_name}`;
        const companyName = clientDetails.person.company_name;
        this.addModal.hide();
        onNotifySuccess(
          `<strong>${
            clientDetails.client_type === 'business'
              ? companyName
              : clientFullName
          }</strong> has been added successfully.`
        );
      })
      .then(() => {
        analytics.track('Broker Portal - Add Client Failed', {
          noninteraction: 1,
        });
        clearClientsList();
        return Promise.all([
          getClients(this.clientsFilter),
          getClientsStats(this.clientsFilter),
        ]);
      })
      .catch(onNotifyError);
  };

  handleClickClient = (e) => {
    const clientId = e.currentTarget.dataset.clientid;
    this.props.history.push(`/dashboard/clients/${clientId}`);
    return false;
  };

  handleUploadCSV = (file) => {
    window.$('.modal').modal('hide');
    this.props.onUploadStart();
    this.props.uploadClientCSV(file);
  };

  handleLoadMore = () => {
    analytics.track('Broker Portal - Load More Clients Clicked');
    this.props.getClients(this.clientsFilter);
  };

  handleUpdateBrokerIsMe = (e) => {
    analytics.track('Broker Portal - Clients Filter Toggled', {
      brokerIsMe: e.target.value === 'true',
    });
    this.setState(
      {
        brokerIsMe: e.target.value,
      },
      () => {
        this.props.clearClientsList();
        this.props.getClientsStats(this.clientsFilter);
        this.props.getClients(this.clientsFilter);
      }
    );
  };

  showAddClientOptionsModal = (e) => {
    analytics.track('Broker Portal - Add Client Options Modal Opened');
    window.$(this.addClientOptionsModal).modal('show');
  };

  showAddModal = (e) => {
    analytics.track('Broker Portal - Add Client Modal Opened', {
      value: parseInt(e.target.dataset.position, 10),
    });
    window.$(this.addClientOptionsModal).modal('hide');
    this.addModal.show();
  };

  renderAvatar = (client) => {
    switch (client.client_type) {
      case 'individual':
        return (
          <>
            <Avatar
              name={`${client.person.first_name} ${client.person.last_name}`}
              className="m-r-10"
              bgColor="#e6e6e6"
              size={44}
            />
            <p className="ClientsIndexView-tableRow-person">
              <strong>
                {client.person.first_name} {client.person.last_name}
              </strong>
              <br />
              {client.person.email}
            </p>
          </>
        );
      case 'business':
        return (
          <>
            <Avatar
              name={client.person.company_name}
              isBusiness
              className="m-r-10"
              bgColor="#e6e6e6"
              size={44}
            />
            <p className="ClientsIndexView-tableRow-person">
              <strong>{client.person.company_name}</strong>
              <br />
              {client.person.email}
            </p>
          </>
        );
      default:
        return null;
    }
  };

  get clientsFilter() {
    if (this.state.brokerIsMe === 'true') {
      return {
        broker_is_me: 'yes',
      };
    } else {
      return {};
    }
  }

  get zeroStateNode() {
    return (
      <div className="ClientsIndexZeroView">
        <div className="col-5">
          <div className="card">
            <h2 className="ClientsIndexZeroView-heading">
              Your client list is empty.
            </h2>
            <p className="ClientsIndexZeroView-subheading">
              Let’s get started. Use this tool to import your client
              information.
            </p>
            <AddClientOptions
              onClickManual={this.showAddModal}
              onUpload={this.handleUploadCSV}
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { clients, clientsStats, isFetchingClients, isUpdatingClient } =
      this.props;

    return (
      <Fragment>
        {clients.length ? (
          <div className="ClientsIndexView container-fluid">
            <div className="card m-t-30">
              <div className="ClientsIndexView-tableTitle card-header">
                <div className="row justify-content-between align-items-center p-l-15 p-r-15">
                  <h2 className="ClientsIndexView-tableTitle-heading">
                    Clients
                    <span className="ClientsIndexView-tableTitle-heading-count">
                      {clientsStats.total_results}
                    </span>
                  </h2>
                  <div className="form-inline">
                    <select
                      className="ClientsIndexView-brokerFilterSelect form-control"
                      name="broker_is_me"
                      value={this.state.brokerIsMe}
                      onChange={this.handleUpdateBrokerIsMe}
                    >
                      <option value="false">All Clients</option>
                      <option value="true">My Clients</option>
                    </select>
                    <button
                      className="btn btn-complete btn-cons"
                      data-position="1"
                      onClick={this.showAddClientOptionsModal}
                    >
                      <i className="fas fa-plus" /> Add
                    </button>
                  </div>
                </div>
              </div>
              <div
                className="card-block no-padding"
                style={{ minHeight: 'calc(100vh - 200px)' }}
              >
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th
                          style={{ width: '20%' }}
                          className="ClientsIndexView-tableHead bold text-black text-uppercase"
                        >
                          Client
                        </th>
                        <th
                          style={{ width: '12%' }}
                          className="ClientsIndexView-tableHead bold text-uppercase"
                        >
                          Added on
                        </th>
                        <th
                          style={{ width: '14%' }}
                          className="ClientsIndexView-tableHead bold text-uppercase"
                        >
                          State
                        </th>
                        <th
                          style={{ width: '12%' }}
                          className="ClientsIndexView-tableHead bold text-uppercase"
                        >
                          Next renewal
                        </th>
                        <th
                          style={{ width: '10%' }}
                          className="ClientsIndexView-tableHead bold text-uppercase"
                        >
                          Policies
                        </th>
                        <th
                          style={{ width: '17%' }}
                          className="ClientsIndexView-tableHead bold text-uppercase"
                        >
                          Broker
                        </th>
                        <th
                          style={{ width: '15%' }}
                          className="ClientsIndexView-tableHead bold text-uppercase"
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr
                          key={client.id}
                          className="ClientsIndexView-tableRow"
                          data-clientid={client.id}
                          onClick={this.handleClickClient}
                        >
                          <td className="v-align-middle">
                            <Link
                              to={`/dashboard/clients/${client.id}`}
                              className="ClientsIndexView-clientCell"
                            >
                              {this.renderAvatar(client)}
                            </Link>
                          </td>
                          <td className="v-align-middle">
                            <p>
                              {moment(client.created_at).format('MMM DD, YYYY')}
                            </p>
                          </td>
                          <td className="v-align-middle">
                            <p>
                              {get(client, 'person.address.region.code') ||
                                'n/a'}
                            </p>
                          </td>
                          <td className="v-align-middle">
                            <p>
                              {(client.next_renewal_date &&
                                moment(
                                  client.next_renewal_date,
                                  'YYYY-MM-DD'
                                ).format('MMM DD, YYYY')) ||
                                'n/a'}
                            </p>
                          </td>
                          <td className="v-align-middle">
                            <p>{client.policies_stats.inforce || 0}</p>
                          </td>
                          <td className="v-align-middle">
                            <p>{client.broker.name}</p>
                          </td>
                          <td className="v-align-middle">
                            <p>{this.getStatus(client)}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {clients.length !== clientsStats.total_results && (
                  <div className="row justify-content-center p-t-30 p-b-30">
                    <button
                      className="btn btn-secondary"
                      onClick={this.handleLoadMore}
                    >
                      <i className="fas fa-chevron-down" /> Load more clients
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          !isFetchingClients && this.zeroStateNode
        )}

        <ModalClientDetails
          isUpdatingClient={isUpdatingClient}
          onSubmit={this.handleAddClient}
          ref={(c) => (this.addModal = c)}
        />
        <ModalPortal>
          <div
            className="modal"
            id="addClientOptionsModal"
            ref={(c) => (this.addClientOptionsModal = c)}
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content-wrapper">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Add new clients</h5>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div className="modal-body p-b-0">
                    <AddClientOptions
                      lightTheme
                      onClickManual={this.showAddModal}
                      onUpload={this.handleUploadCSV}
                    />
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      data-dismiss="modal"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      </Fragment>
    );
  }
}

ClientsIndexView.propTypes = propTypes;
ClientsIndexView.defaultProps = defaultProps;

export default ClientsIndexView;
